import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
	ArrowLeft,
	Braces,
	ListChecks,
	MessageCircle,
	Trash2,
} from "lucide-react"
import * as React from "react"
import { CreateIpBanDialog } from "@/components/ip-bans/create-ip-ban-dialog"
import { JsonViewCard } from "@/components/marketplace/json-view-card"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
	type AccountAction,
	AccountStatusDialog,
} from "@/components/users/account-status-dialog"
import { PurgeScammerDialog } from "@/components/users/purge-scammer-dialog"
import { MODERATION_SEARCH_DEFAULTS } from "@/config/search-defaults"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { warningReasonsFromTags } from "@/features/moderation-admin/moderation-labels"
import { toSelectedCase } from "@/features/moderation-admin/moderation-selection"
import {
	ResolveCasesDialog,
	type ResolveDecision,
} from "@/features/moderation-admin/ResolveCasesDialog"
import { authorScope } from "@/features/moderation-case/account-scope"
import { ConversationTab } from "@/features/moderation-case/ConversationTab"
import { ModerationCaseActionBar } from "@/features/moderation-case/ModerationCaseActionBar"
import { ModerationCasePageHeader } from "@/features/moderation-case/ModerationCasePageHeader"
import { ModerationCaseRail } from "@/features/moderation-case/ModerationCaseRail"
import { RemovedContentList } from "@/features/moderation-case/RemovedContentList"
import { VerdictsTable } from "@/features/moderation-case/VerdictsTable"
import { WarnUserDialog } from "@/features/moderation-case/WarnUserDialog"
import { useModerationCase } from "@/queries/marketplace"
import type { ModerationCounterparty } from "@/types/marketplace.types"

const TABS = ["verdicts", "removed", "conversation", "json"] as const

type CaseTab = (typeof TABS)[number]

function isCaseTab(value: unknown): value is CaseTab {
	return (
		typeof value === "string" && (TABS as readonly string[]).includes(value)
	)
}

export const Route = createFileRoute("/moderation/$caseId/")({
	component: ModerationCasePage,
	validateSearch: (
		search: Record<string, unknown>,
	): { tab?: CaseTab; conversation?: string } => ({
		tab: isCaseTab(search.tab) ? search.tab : undefined,
		conversation:
			typeof search.conversation === "string" ? search.conversation : undefined,
	}),
})

function ModerationCasePage() {
	const { caseId } = Route.useParams()
	const searchParams = Route.useSearch()
	const navigate = useNavigate()
	const { hasRole } = useAdminRolesContext()
	const canModerate = hasRole(AdminRole.MarketplaceUserManagement)
	const { caseDetail, isLoading, error, refetch } = useModerationCase({
		id: caseId,
	})

	const activeTab: CaseTab = searchParams.tab ?? "verdicts"
	const setSearch = (next: { tab?: CaseTab; conversation?: string }) =>
		navigate({
			to: "/moderation/$caseId",
			params: { caseId },
			search: {
				tab: next.tab === "verdicts" ? undefined : next.tab,
				conversation:
					next.tab === "conversation" ? next.conversation : undefined,
			},
			replace: true,
		})

	const [resolveDialog, setResolveDialog] = React.useState<{
		open: boolean
		decision: ResolveDecision
	}>({ open: false, decision: "dismiss" })
	const [statusDialog, setStatusDialog] = React.useState<{
		open: boolean
		action: AccountAction | null
	}>({ open: false, action: null })
	const [banIpOpen, setBanIpOpen] = React.useState(false)
	const [purgeOpen, setPurgeOpen] = React.useState(false)
	const [warnParty, setWarnParty] =
		React.useState<ModerationCounterparty | null>(null)
	const [suspendParty, setSuspendParty] =
		React.useState<ModerationCounterparty | null>(null)

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-4 sm:p-6">
				<div className="flex flex-col gap-2">
					<Skeleton className="h-7 w-72 max-w-full" />
					<Skeleton className="h-5 w-96 max-w-full" />
				</div>
				<div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
					<div className="flex flex-col gap-1">
						{Array.from({ length: 8 }, (_, index) => (
							<Skeleton key={index} className="h-9 w-full" />
						))}
					</div>
					<Skeleton className="h-80" />
				</div>
			</div>
		)
	}

	if (error || !caseDetail) {
		return (
			<div className="flex flex-col items-start gap-3 p-4 sm:p-6">
				<div className="flex flex-col gap-1 rounded-lg bg-bad-soft px-4 py-3">
					<span className="font-medium text-bad">Case not found</span>
					<span className="text-muted-foreground">
						{error?.message || `No moderation case matches ${caseId}.`}
					</span>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" asChild>
						<Link to="/moderation" search={MODERATION_SEARCH_DEFAULTS}>
							<ArrowLeft />
							Back to the queue
						</Link>
					</Button>
					<Button variant="ghost" onClick={() => refetch()}>
						Retry
					</Button>
				</div>
			</div>
		)
	}

	const { case: c, author, context } = caseDetail
	const caseOpen = c.status !== "resolved"
	const selectedCase = toSelectedCase(c)

	const handleAddNote = () => {
		setTimeout(() => {
			document
				.getElementById("admin-notes")
				?.scrollIntoView({ behavior: "smooth", block: "center" })
		}, 50)
	}

	return (
		<div key={c.id} className="px-4 pb-10 sm:px-6">
			<ModerationCasePageHeader
				detail={caseDetail}
				actionBar={
					<ModerationCaseActionBar
						detail={caseDetail}
						canModerate={canModerate}
						actions={{
							onConfirm: () =>
								setResolveDialog({ open: true, decision: "confirm" }),
							onDismiss: () =>
								setResolveDialog({ open: true, decision: "dismiss" }),
							onWarn: () => setResolveDialog({ open: true, decision: "warn" }),
							onSuspend: () =>
								setStatusDialog({ open: true, action: "suspend" }),
							onClear: () =>
								setStatusDialog({ open: true, action: "unsuspend" }),
							onBanIp: () => setBanIpOpen(true),
							onPurge: () => setPurgeOpen(true),
							onAddNote: handleAddNote,
						}}
					/>
				}
			/>

			<div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
				<div className="min-w-0">
					<Tabs
						value={activeTab}
						onValueChange={(tab) =>
							setSearch({
								tab: tab as CaseTab,
								conversation: searchParams.conversation,
							})
						}
						className="gap-6"
					>
						<TabsList variant="line">
							<TabsTrigger value="verdicts">
								<ListChecks />
								Verdicts
								<span className="font-mono text-[11px] text-meta tabular-nums">
									{caseDetail.verdicts.length}
								</span>
							</TabsTrigger>
							<TabsTrigger value="removed">
								<Trash2 />
								Removed
								<span className="font-mono text-[11px] text-meta tabular-nums">
									{caseDetail.removedMessages.length}
								</span>
							</TabsTrigger>
							<TabsTrigger value="conversation">
								<MessageCircle />
								Conversation
							</TabsTrigger>
							<TabsTrigger value="json">
								<Braces />
								JSON
							</TabsTrigger>
						</TabsList>

						<TabsContent value="verdicts" className="mt-0">
							<VerdictsTable verdicts={caseDetail.verdicts} caseId={c.id} />
						</TabsContent>

						<TabsContent value="removed" className="mt-0">
							<RemovedContentList messages={caseDetail.removedMessages} />
						</TabsContent>

						<TabsContent value="conversation" className="mt-0">
							{activeTab === "conversation" && (
								<ConversationTab
									detail={caseDetail}
									requestedKey={searchParams.conversation}
									onSelect={(key) =>
										setSearch({ tab: "conversation", conversation: key })
									}
								/>
							)}
						</TabsContent>

						<TabsContent value="json" className="mt-0">
							<JsonViewCard
								title="Full case JSON"
								description="The admin detail payload, for debugging"
								data={caseDetail}
							/>
						</TabsContent>
					</Tabs>
				</div>

				<ModerationCaseRail
					detail={caseDetail}
					canModerate={canModerate}
					onWarnCounterparty={setWarnParty}
					onSuspendCounterparty={setSuspendParty}
				/>
			</div>

			{canModerate && (
				<>
					<ResolveCasesDialog
						open={resolveDialog.open}
						onOpenChange={(open) =>
							setResolveDialog((prev) => ({ ...prev, open }))
						}
						decision={resolveDialog.decision}
						cases={[selectedCase]}
					/>
					<AccountStatusDialog
						open={statusDialog.open}
						onOpenChange={(open) =>
							setStatusDialog((prev) => ({ ...prev, open }))
						}
						userId={author.id}
						username={author.username}
						action={statusDialog.action}
						scope={authorScope(author)}
						moderationCaseId={caseOpen ? c.id : undefined}
					/>
					{context.lastIp && (
						<CreateIpBanDialog
							open={banIpOpen}
							onOpenChange={setBanIpOpen}
							cidr={context.lastIp}
						/>
					)}
					<PurgeScammerDialog
						open={purgeOpen}
						onOpenChange={setPurgeOpen}
						userId={author.id}
						username={author.username}
						moderationCaseId={caseOpen ? c.id : undefined}
					/>
					{warnParty && (
						<WarnUserDialog
							open
							onOpenChange={(open) => {
								if (!open) setWarnParty(null)
							}}
							userId={warnParty.user.id}
							username={warnParty.user.username}
							defaultReasons={warningReasonsFromTags(c.tags)}
							caseId={c.id}
							referenceId={warnParty.conversation.channelId}
						/>
					)}
					{suspendParty && (
						<AccountStatusDialog
							open
							onOpenChange={(open) => {
								if (!open) setSuspendParty(null)
							}}
							userId={suspendParty.user.id}
							username={suspendParty.user.username}
							action="suspend"
							scope={suspendParty.role}
						/>
					)}
				</>
			)}
		</div>
	)
}
